import moment from "moment";
import { notification } from "antd";
import { WDQMS_API, fetchData } from "../../utils/api";
import * as actionTypes from "./actionTypes";

/**
 * Dictionary for BUOY report types with their supported period types and defaults
 */
export const BUOY_REPORT_TYPES = {
  traditional: {
    periodTypes: ["six_hour", "daily", "monthly"],
    defaultPeriodType: "six_hour",
    url: {
      six_hour: "sixhour-periods",
      daily: "daily-periods",
      monthly: "monthly-periods"
    }
  }
};

/**
 * Sets the period data key
 * @param {Object} data - The data to set
 * @returns {Object} The action object
 */
const setPeriodDataKey = data => {
  return {
    type: actionTypes.SET_PERIOD_DATA_KEY,
    payload: data
  };
};

/**
 * Sets the property
 * @param {Object} data - The data to set
 * @returns {Object} The action object
 */
export const setProperty = data => {
  return { type: actionTypes.SET_PROPERTY, payload: data };
};

/**
 * Updates period types
 * @param {Object} data - The data to update
 * @returns {Object} The action object
 */
export const updatePeriodTypes = data => {
  return {
    type: actionTypes.UPDATE_PERIOD_TYPES,
    payload: data
  };
};

/**
 * Sets error 404
 * @returns {Object} The action object
 */
const setError404 = () => {
  return {
    type: actionTypes.SET_ERROR_404
  };
};

/**
 * Checks if an object is empty
 * @param {Object} obj - The object to check
 * @returns {boolean} Whether the object is empty
 */
const isEmpty = obj => {
  return Object.keys(obj).length === 0;
};

let sixHourFetched = false;
let dailyFetched = false;

/**
 * Fetches data from the API for BUOY
 * @param {Object} payload - The payload containing the file type and parameters
 * @param {string} pageType - The page type
 * @returns {Function} A function that takes dispatch and performs the API calls
 */
export const fetchBuoyDataFromApi = (payload, pageType = "") => {
  // differentiate between requests coming from the map and from the database status
  if (pageType === "DB_STATUS") {
    pageType = "&page_type=DB_STATUS";
  }
  
  return dispatch => {
    // If we have URL params, set them to the selected params for the menu and map
    if (!isEmpty(payload.params)) {
      dispatch(
        setProperty({
          type: payload.fileType,
          key: "selectedPeriodType",
          value: payload.params.periodType
        })
      );

      if (payload.fileType === "buoy") {
        dispatch(
          setProperty({
            type: payload.fileType,
            key: "selectedReport",
            value: "quality"
          })
        );
      }

      // Update the variable only if it is in the params
      if (payload.params.variable) {
        dispatch(
          setProperty({
            type: payload.fileType,
            key: "selectedVariable",
            value: payload.params.variable
          })
        );
      }

      dispatch(
        setProperty({
          type: payload.fileType,
          key: "selectedCenter",
          value: payload.params.center
        })
      );

      if (payload.params.center !== "all") {
        dispatch(
          setProperty({
            type: payload.fileType,
            key: "combinedMode",
            value: false
          })
        );
      }

      dispatch(
        setProperty({
          type: payload.fileType,
          key: "selectedDate",
          value: payload.params.date
        })
      );
    }

    if (!payload.alreadyFetched) {
      fetchBuoySixhourPeriod(payload, pageType, dispatch);
    }
  };
};

/**
 * Fetches six-hour periods for BUOY
 * @param {Object} payload - The payload containing file type and parameters
 * @param {string} pageType - The page type
 * @param {Function} dispatch - The dispatch function
 */
function fetchBuoySixhourPeriod(payload, pageType, dispatch) {
  return fetchData(
    `${WDQMS_API}/sixhour-periods/?file_type=${payload.fileType}&format=json${pageType}`
  )
    .then(response => {
      // response.data.length === 0 -> NO DATA
      if (response.data.length === 0) {
        dispatch(
          setPeriodDataKey({
            subObj: "periodData",
            type: payload.fileType,
            period: "six_hour",
            key: "noData",
            value: true
          })
        );
        // set the data (for a particular fileType) as fetched
        // so that won't be fetched a second time
        dispatch(
          setProperty({
            type: payload.fileType,
            key: "dataFetched",
            value: true
          })
        );
      } else {
        sixHourFetched = true;
        if (isEmpty(payload.params)) {
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedPeriodType",
              value: "six_hour"
            })
          );
        }

        dispatch(
          updatePeriodTypes({
            type: payload.fileType,
            value: "six_hour"
          })
        );

        let tempObj = {};
        for (let p of response.data.results) {
          if (!tempObj[p.center]) {
            tempObj[p.center] = {};
          }
          if (!tempObj[p.center][moment.utc(p.date).format("YYYY-MM-DD")]) {
            tempObj[p.center][moment.utc(p.date).format("YYYY-MM-DD")] = {
              "00": { available: false, id: null },
              "06": { available: false, id: null },
              "12": { available: false, id: null },
              "18": { available: false, id: null }
            };
          }
          tempObj[p.center][moment.utc(p.date).format("YYYY-MM-DD")][
            moment.utc(p.date).format("HH")
          ].available = true;
          tempObj[p.center][moment.utc(p.date).format("YYYY-MM-DD")][
            moment.utc(p.date).format("HH")
          ].id = p.id;
        }
        // The object that contains the dates for the 'combined' version (all centers)
        let objectAll = {};
        // loop through all centers
        for (let ct of Object.keys(tempObj)) {
          // loop through all dates
          for (let d of Object.keys(tempObj[ct])) {
            // if objectAll doesn't have the key 'd'
            if (!Object.keys(objectAll).includes(d)) {
              objectAll[d] = {};
              // loop through the 4 six-hour periods
              for (let p of Object.keys(tempObj[ct][d])) {
                if (tempObj[ct][d][p].available) {
                  objectAll[d][p] = {};
                  objectAll[d][p].available = true;
                  objectAll[d][p].id = {};
                  objectAll[d][p].id[ct] = tempObj[ct][d][p].id;
                } else {
                  objectAll[d][p] = {
                    available: false,
                    id: {}
                  };
                }
              }
            } else {
              for (let p of Object.keys(tempObj[ct][d])) {
                if (tempObj[ct][d][p].available) {
                  objectAll[d][p].available = true;
                  objectAll[d][p].id[ct] = tempObj[ct][d][p].id;
                }
              }
            }
          }
        }
        tempObj.all = objectAll;

        dispatch(
          setPeriodDataKey({
            subObj: "periodData",
            type: payload.fileType,
            period: "six_hour",
            key: "dates",
            value: tempObj
          })
        );
        dispatch(
          setPeriodDataKey({
            subObj: "periodData",
            type: payload.fileType,
            period: "six_hour",
            key: "centers",
            // case insensitive sort so that "all" is first element
            value: Object.keys(tempObj).sort(function(a, b) {
              return a.toLowerCase().localeCompare(b.toLowerCase());
            })
          })
        );

        let latestDate;
        let availablePeriods;
        if (isEmpty(payload.params)) {
          const firstCenter = Object.keys(tempObj).sort(function(a, b) {
            return a.toLowerCase().localeCompare(b.toLowerCase());
          })[0];
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedCenter",
              value: firstCenter
            })
          );

          latestDate = Object.keys(tempObj[firstCenter]).sort()[
            Object.keys(tempObj[firstCenter]).length - 1
          ];
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedDate",
              value: latestDate
            })
          );

          availablePeriods = tempObj[firstCenter][latestDate];

          const arrAvailablePeriods = Object.keys(availablePeriods)
            .sort()
            .filter(p => availablePeriods[p].available);

          dispatch(
            setProperty({
              type: payload.fileType,
              key: "selectedSixHPeriod",
              value: arrAvailablePeriods[arrAvailablePeriods.length - 1]
            })
          );
        } else if (payload.params.periodType === "six_hour") {
          latestDate = payload.params.date;
          // check if the date exist
          if (tempObj[payload.params.center][latestDate]) {
            availablePeriods = tempObj[payload.params.center][latestDate];

            dispatch(
              setProperty({
                type: payload.fileType,
                key: "selectedSixHPeriod",
                value: payload.params.period
              })
            );

            // if the date doesn't exist, trigger an error
          } else {
            dispatch(setError404());
          }

          // if the six-hour period is not available for this date,
          // also trigger an error
          if (
            tempObj[payload.params.center][latestDate] &&
            typeof payload.params.period !== "undefined"
          ) {
            if (!availablePeriods[payload.params.period].available) {
              dispatch(setError404());
            }
          }
        }

        dispatch(
          setProperty({
            type: payload.fileType,
            key: "currentSixHPeriods",
            value: availablePeriods
          })
        );

        if (
          isEmpty(payload.params) ||
          payload.params.periodType === "six_hour"
        ) {
          dispatch(
            setProperty({
              type: payload.fileType,
              key: "readyToFillMenu",
              value: true
            })
          );
        }

        // set the data (for a particular fileType) as fetched
        // so that won't be fetched a second time
        dispatch(
          setProperty({
            type: payload.fileType,
            key: "dataFetched",
            value: true
          })
        );
      }
    })
    .catch(error => {
      // If there are URL params requesting the six-hour data
      // and an error while fetching the six-hour data, trigger an error
      if (
        !isEmpty(payload.params) &&
        payload.params.periodType === "six_hour"
      ) {
        dispatch(setError404());
      }

      sixHourFetched = false;

      notification.error({
        message: "Error fetching API",
        description: "There was an error while fetching the six-hour data",
        top: 100,
        duration: 0
      });
      dispatch(
        setPeriodDataKey({
          subObj: "periodData",
          type: payload.fileType,
          period: "six_hour",
          key: "errorFetchingApi",
          value: true
        })
      );
    })
    .then(() => {
      fetchData(
        `${WDQMS_API}/daily-periods/?file_type=${payload.fileType}${pageType}`
      )
        .then(response => {
          if (response.data.length === 0) {
            dispatch(
              setPeriodDataKey({
                subObj: "periodData",
                type: payload.fileType,
                period: "daily",
                key: "noData",
                value: true
              })
            );
          } else {
            dailyFetched = true;
            if (!sixHourFetched) {
              dispatch(
                setProperty({
                  type: payload.fileType,
                  key: "selectedPeriodType",
                  value: "daily"
                })
              );
            }
            dispatch(
              updatePeriodTypes({
                type: payload.fileType,
                value: ["daily"]
              })
            );
            let tempObj = {};
            for (let p of response.data) {
              if (!tempObj[p.center]) {
                tempObj[p.center] = {};
              }
              tempObj[p.center][moment.utc(p.date).format("YYYY-MM-DD")] = {
                id: p.id
              };
            }
            let objectAll = {};
            for (let ct of Object.keys(tempObj)) {
              for (let d of Object.keys(tempObj[ct])) {
                if (!Object.keys(objectAll).includes(d)) {
                  objectAll[d] = {};
                }
                objectAll[d][ct] = tempObj[ct][d].id;
              }
            }
            tempObj.all = objectAll;
            dispatch(
              setPeriodDataKey({
                subObj: "periodData",
                type: payload.fileType,
                period: "daily",
                key: "dates",
                value: tempObj
              })
            );
            dispatch(
              setPeriodDataKey({
                subObj: "periodData",
                type: payload.fileType,
                period: "daily",
                key: "centers",
                value: Object.keys(tempObj).sort(function(a, b) {
                  return a.toLowerCase().localeCompare(b.toLowerCase());
                })
              })
            );
            if (
              !isEmpty(payload.params) &&
              payload.params.periodType === "daily"
            ) {
              dispatch(
                setProperty({
                  type: payload.fileType,
                  key: "selectedCenter",
                  value: payload.params.center
                })
              );
              if (!tempObj[payload.params.center][payload.params.date]) {
                const latestDate = Object.keys(
                  tempObj[payload.params.center]
                ).sort()[
                  Object.keys(tempObj[payload.params.center]).length - 1
                ];
                dispatch(
                  setProperty({
                    type: payload.fileType,
                    key: "selectedDate",
                    value: latestDate
                  })
                );
              } else {
                dispatch(
                  setProperty({
                    type: payload.fileType,
                    key: "selectedDate",
                    value: payload.params.date
                  })
                );
              }
            } else if (!sixHourFetched) {
              const firstCenter = Object.keys(tempObj).sort(function(a, b) {
                return a.toLowerCase().localeCompare(b.toLowerCase());
              })[0];
              dispatch(
                setProperty({
                  type: payload.fileType,
                  key: "selectedCenter",
                  value: firstCenter
                })
              );
              const latestDate = Object.keys(tempObj[firstCenter]).sort()[
                Object.keys(tempObj[firstCenter]).length - 1
              ];
              dispatch(
                setProperty({
                  type: payload.fileType,
                  key: "selectedDate",
                  value: latestDate
                })
              );
            }
            if (
              isEmpty(payload.params) ||
              payload.params.periodType === "daily"
            ) {
              dispatch(
                setProperty({
                  type: payload.fileType,
                  key: "readyToFillMenu",
                  value: true
                })
              );
            }
          }
        })
        .catch(error => {
          if (
            !isEmpty(payload.params) &&
            payload.params.periodType === "daily"
          ) {
            dispatch(setError404());
          }
          notification.error({
            message: "Error fetching API",
            description: "There was an error while fetching the daily data",
            top: 100,
            duration: 0
          });
          dispatch(
            setPeriodDataKey({
              subObj: "periodData",
              type: payload.fileType,
              period: "daily",
              key: "errorFetchingApi",
              value: true
            })
          );
        })
        .then(() => {
          fetchData(
            `${WDQMS_API}/monthly-periods/?file_type=${payload.fileType}${pageType}`
          ).then(response => {
            if (response.data.length === 0) {
              dispatch(
                setPeriodDataKey({
                  subObj: "periodData",
                  type: payload.fileType,
                  period: "monthly",
                  key: "noData",
                  value: true
                })
              );
            } else {
              if (!dailyFetched) {
                dispatch(
                  setProperty({
                    type: payload.fileType,
                    key: "selectedPeriodType",
                    value: "monthly"
                  })
                );
              }
              dispatch(
                updatePeriodTypes({
                  type: payload.fileType,
                  value: ["monthly"]
                })
              );
              let tempObj = {};
              for (let p of response.data) {
                if (!tempObj[p.center]) {
                  tempObj[p.center] = {};
                }
                tempObj[p.center][moment.utc(p.date).format("YYYY-MM")] = {
                  id: p.id
                };
              }
              let objectAll = {};
              for (let ct of Object.keys(tempObj)) {
                for (let d of Object.keys(tempObj[ct])) {
                  if (!Object.keys(objectAll).includes(d)) {
                    objectAll[d] = {};
                  }
                  objectAll[d][ct] = tempObj[ct][d].id;
                }
              }
              tempObj.all = objectAll;
              dispatch(
                setPeriodDataKey({
                  subObj: "periodData",
                  type: payload.fileType,
                  period: "monthly",
                  key: "dates",
                  value: tempObj
                })
              );
              dispatch(
                setPeriodDataKey({
                  subObj: "periodData",
                  type: payload.fileType,
                  period: "monthly",
                  key: "centers",
                  value: Object.keys(tempObj).sort(function(a, b) {
                    return a.toLowerCase().localeCompare(b.toLowerCase());
                  })
                })
              );
              if (
                !isEmpty(payload.params) &&
                payload.params.periodType === "monthly"
              ) {
                dispatch(
                  setProperty({
                    type: payload.fileType,
                    key: "selectedCenter",
                    value: payload.params.center
                  })
                );
                if (!tempObj[payload.params.center][payload.params.date]) {
                  const latestDate = Object.keys(
                    tempObj[payload.params.center]
                  ).sort()[
                    Object.keys(tempObj[payload.params.center]).length - 1
                  ];
                  dispatch(
                    setProperty({
                      type: payload.fileType,
                      key: "selectedDate",
                      value: latestDate
                    })
                  );
                } else {
                  dispatch(
                    setProperty({
                      type: payload.fileType,
                      key: "selectedDate",
                      value: payload.params.date
                    })
                  );
                }
              } else if (!dailyFetched) {
                const firstCenter = Object.keys(tempObj).sort(function(a, b) {
                  return a.toLowerCase().localeCompare(b.toLowerCase());
                })[0];
                dispatch(
                  setProperty({
                    type: payload.fileType,
                    key: "selectedCenter",
                    value: firstCenter
                  })
                );
                const latestDate = Object.keys(tempObj[firstCenter]).sort()[
                  Object.keys(tempObj[firstCenter]).length - 1
                ];
                dispatch(
                  setProperty({
                    type: payload.fileType,
                    key: "selectedDate",
                    value: latestDate
                  })
                );
              }
              if (
                isEmpty(payload.params) ||
                payload.params.periodType === "monthly"
              ) {
                dispatch(
                  setProperty({
                    type: payload.fileType,
                    key: "readyToFillMenu",
                    value: true
                  })
                );
              }
            }
          });
        });
    });
}
